import { useState, useEffect, memo } from 'react';
import { motion } from 'framer-motion';
import { getClientePrimeiroNome } from '../../domain/usecases/clienteDisplay';
import { EMOJIS_REACAO, EMOJIS_COPA_REACAO, type EmojiReacao } from '../hooks/useReacaoAoVivo';

const BandeiraFlag = (
  <svg viewBox="0 0 32 22" width="24" height="24" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius: 3, display: 'block' }}>
    <rect width="32" height="22" fill="#009c3b"/>
    <polygon points="16,2 30,11 16,20 2,11" fill="#FFDF00"/>
    <circle cx="16" cy="11" r="5.5" fill="#002776"/>
    <path d="M11 12.5 Q16 10 21 12.5" stroke="white" strokeWidth="1.2" fill="none"/>
    <circle cx="16" cy="9.5" r="0.6" fill="white"/>
    <circle cx="13.5" cy="11.2" r="0.5" fill="white"/>
    <circle cx="18.5" cy="11.2" r="0.5" fill="white"/>
    <circle cx="15" cy="12.8" r="0.5" fill="white"/>
    <circle cx="17" cy="12.8" r="0.5" fill="white"/>
  </svg>
);

const SHADOW_DEFAULT = '0 0 0 1px rgba(249,115,22,0.15), 0 25px 50px -12px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.15)';

function useProgressoMusica(duracaoSegundos: number | null | undefined, iniciadaEm: string | null | undefined) {
  const [progresso, setProgresso] = useState(0);
  const [restanteSeg, setRestanteSeg] = useState<number | null>(null);
  // true no salto pro valor real (mount / troca de música) — evita a barra
  // "animar" do 0% até a posição atual da música com a transição de 1s do tick normal.
  const [instantaneo, setInstantaneo] = useState(true);

  useEffect(() => {
    if (!duracaoSegundos || !iniciadaEm) {
      setProgresso(0);
      setRestanteSeg(null);
      setInstantaneo(true);
      return;
    }
    const inicio = new Date(iniciadaEm).getTime();
    const calcular = () => {
      const decorrido = (Date.now() - inicio) / 1000;
      setProgresso(Math.min(decorrido / duracaoSegundos, 1));
      setRestanteSeg(Math.max(Math.ceil(duracaoSegundos - decorrido), 0));
    };
    setInstantaneo(true);
    calcular();
    // Duplo rAF: garante que o salto instantâneo já pintou antes de reativar
    // a transição suave dos próximos ticks de 1s.
    let religarId2 = 0;
    const religarId1 = requestAnimationFrame(() => {
      religarId2 = requestAnimationFrame(() => setInstantaneo(false));
    });
    const id = setInterval(calcular, 1_000);
    return () => {
      clearInterval(id);
      cancelAnimationFrame(religarId1);
      cancelAnimationFrame(religarId2);
    };
  }, [duracaoSegundos, iniciadaEm]);

  return { progresso, restanteSeg, instantaneo };
}

function fmt(s: number): string {
  return `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
}

/**
 * Palco ao vivo — música tocando na TV.
 * Âncora: badge “No ar” / “Sua música”.
 */
function NowPlayingCardImpl({
  titulo,
  thumbnail,
  meuPedido = false,
  clienteNome,
  duracaoSegundos,
  iniciadaEm,
  progressGradient = 'from-orange-400 to-rose-400',
  nowPlayingGlow,
  mostrarReacoes = false,
  temaCopa = false,
  onReagir,
}: {
  titulo:             string;
  thumbnail:          string;
  meuPedido?:         boolean;
  clienteNome?:       string | null;
  duracaoSegundos?:   number | null;
  iniciadaEm?:        string | null;
  progressGradient?:  string;
  nowPlayingGlow?:    string;
  mostrarReacoes?:    boolean;
  temaCopa?:          boolean;
  onReagir?:          (emoji: EmojiReacao) => void;
}) {
  const primeiroNome = getClientePrimeiroNome(clienteNome);
  const { progresso, restanteSeg, instantaneo } = useProgressoMusica(duracaoSegundos, iniciadaEm);
  const [pressedReacao, setPressedReacao] = useState<EmojiReacao | null>(null);
  const temProgresso = restanteSeg !== null && duracaoSegundos != null && iniciadaEm != null;
  const decorridoSeg = duracaoSegundos && restanteSeg !== null
    ? Math.max(0, duracaoSegundos - restanteSeg)
    : null;

  return (
    <div
      className="relative overflow-hidden rounded-3xl"
      style={{ boxShadow: nowPlayingGlow ?? SHADOW_DEFAULT }}
      aria-label={`Tocando agora: ${titulo}`}
      role="region"
    >
      <div className="absolute inset-0 scale-110">
        <img src={thumbnail} alt="" aria-hidden className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/70 backdrop-blur-lg" />
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

      <div className="relative p-4">
        <div className="flex items-center gap-3">
          <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-2xl shadow-xl shadow-black/50 ring-1 ring-white/10">
            <img src={thumbnail} alt={titulo} className="h-full w-full object-cover" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <div className="flex h-4 items-end gap-[3px]" aria-hidden>
                {[3, 6, 4, 7, 3, 5].map((h, i) => (
                  <motion.div
                    key={i}
                    className="w-[3px] rounded-full bg-white"
                    style={{ height: `${h * 2}px`, transformOrigin: 'bottom' }}
                    animate={{ scaleY: [0.5, 1] }}
                    transition={{
                      repeat: Infinity,
                      repeatType: 'reverse',
                      delay: i * 0.06,
                      duration: 0.35 + i * 0.09,
                      ease: 'easeInOut',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <div key={String(meuPedido)} className="mb-1.5 flex animate-scale-in items-center gap-1.5">
              <span className={`
                inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[10px] font-bold
                ${meuPedido
                  ? 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30'
                  : 'bg-orange-500/15 text-orange-400 ring-1 ring-orange-500/30'}
              `}>
                <span className={`h-1.5 w-1.5 animate-pulse rounded-full ${meuPedido ? 'bg-emerald-400' : 'bg-orange-400'}`} />
                {meuPedido
                  ? (primeiroNome ? `${primeiroNome} · sua música` : 'Sua música no ar')
                  : 'No ar'}
              </span>
            </div>
            <p className="line-clamp-2 text-base font-bold leading-snug text-white drop-shadow-md">{titulo}</p>
            {temProgresso && restanteSeg !== null && (
              <p className="mt-1 font-mono text-xs tabular-nums text-white/55">
                próxima em <span className="font-semibold text-white/70">{fmt(restanteSeg)}</span>
              </p>
            )}
          </div>
        </div>

        {temProgresso && decorridoSeg !== null && duracaoSegundos && (
          <div className="mt-4">
            <div className="relative h-1 overflow-hidden rounded-full bg-white/10">
              <div
                className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${progressGradient} ease-linear ${instantaneo ? '' : 'transition-all duration-1000'}`}
                style={{ width: `${progresso * 100}%` }}
              />
              <div
                className={`absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 animate-pulse-slow rounded-full bg-white ease-linear ${instantaneo ? '' : 'transition-all duration-1000'}`}
                style={{ left: `${progresso * 100}%` }}
              />
            </div>
            <div className="mt-1.5 flex justify-between">
              <span className="font-mono text-[11px] tabular-nums text-white/55">{fmt(decorridoSeg)}</span>
              <span className="font-mono text-[11px] tabular-nums text-white/55">{fmt(duracaoSegundos)}</span>
            </div>
          </div>
        )}

        {mostrarReacoes && onReagir && (
          <div className="mt-4 border-t border-white/10 pt-4">
            <p className="mb-2.5 text-center text-xs font-medium text-white/55">
              Reagir à música
            </p>
            <div className="flex items-center justify-between gap-1">
              {(temaCopa ? EMOJIS_COPA_REACAO : EMOJIS_REACAO).map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  onPointerDown={() => { setPressedReacao(emoji); onReagir(emoji); }}
                  onPointerUp={() => setPressedReacao(null)}
                  onPointerCancel={() => setPressedReacao(null)}
                  onPointerLeave={() => setPressedReacao(null)}
                  className={`flex min-h-11 min-w-11 flex-1 items-center justify-center rounded-xl py-2.5 select-none transition-all duration-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60 ${
                    pressedReacao === emoji ? 'scale-[0.88] bg-white/20' : 'bg-white/10'
                  }`}
                  style={{ touchAction: 'manipulation' }}
                  aria-label={`Reagir com ${emoji}`}
                >
                  <span style={{ fontSize: 22, lineHeight: 1 }}>
                    {emoji === '🇧🇷' ? BandeiraFlag : emoji}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const NowPlayingCard = memo(NowPlayingCardImpl);
